import { useState } from 'react'
import { useForm } from '../../../hooks'
import toast from 'react-hot-toast'
import TextField from '../../Form/TextField'
import TextAreaField from '../../Form/TextAreaField'
import ColorField from '../../Form/ColorField'
import Button from '../../Button'
import { getDownloadURL, getStorage, ref, uploadBytes } from 'firebase/storage'
import { app } from '../../../firebase'
import { generateImageThumbnail } from '../../../hooks/firebaseService'

export function TrackingPinModal({
  latitude,
  longitude,
  onMarkerCreated,
  activeTrackId
}) {
  const [loading, setLoading] = useState(false)
  const [images, setImages] = useState([])
  const [getFieldProps, formData, formError] = useForm()
  const INCIDENT_MAP_MARKER_COLORS = [
    '#f44336',
    '#ff9800',
    '#4caf50'
    // '#eb144c',
    // '#ff6900',
    // '#00d084',
    // '#9900ef',
    // '#00d1ff'
  ]

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files)

    // Validate file types and sizes
    const validFiles = files.filter((file) => {
      const isValidType = file.type.startsWith('image/')
      const isValidSize = file.size <= 5 * 1024 * 1024 // 5MB limit

      if (!isValidType) {
        toast.error('this formate will not acceptable')
        return false
      }
      if (!isValidSize) {
        toast.error(`${file.name} is too large (max 5MB)`)
        return false
      }
      return true
    })

    // Convert images to base64 for storage
    validFiles.forEach((file) => {
      const reader = new FileReader()
      reader.onload = (e) => {
        setImages((prev) => [
          ...prev,
          {
            name: file.name,
            data: e.target.result,
            type: file.type,
            size: file.size
          }
        ])
      }
      reader.readAsDataURL(file)
    })
  }

  const removeImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index))
  }

  // In TrackingPinModal component, replace the handleSubmit function:

  async function handleSubmit(e) {
    e.preventDefault()
    const { label, description, color } = formData

    if (!label || !color) {
      toast.error('Please fill in all required fields')
      return
    }

    setLoading(true)

    try {
      let imageUrls = []

      // Upload images to Firebase if there are any
      if (images.length > 0) {
        // Assuming you have a function to upload images to Firebase
        // You'll need to implement this based on your Firebase setup
        imageUrls = await uploadImagesToFirebase(images, activeTrackId)
      }

      // Create pin data with Firebase image URLs
      const pinData = {
        latitude,
        longitude,
        label: label.toUpperCase(),
        description,
        color,
        images: imageUrls, // Now storing URLs instead of base64 data
        createdAt: new Date().toISOString(),
        type: 'tracking-pin'
      }

      onMarkerCreated(pinData)
    } catch (err) {
      console.error('Error creating pin:', err)
      toast.error('Failed to create pin')
    } finally {
      setLoading(false)
    }
  }

  // Add this function to handle Firebase image uploads
  async function uploadImagesToFirebase(images, trackId) {
    try {
      // Initialize Firebase Storage
      const storage = getStorage(app)

      const uploadPromises = images.map(async (image, index) => {
        try {
          // Convert base64 to blob
          const response = await fetch(image.data)
          const blob = await response.blob()

          // Create Firebase storage reference
          const timestamp = Date.now()
          const imagePath = `${
            process.env.REACT_APP_ENV === 'production' ? 'prod' : 'stage'
          }tracks/${trackId}/${timestamp}_${index}.jpg`
          const thumbPath = `${
            process.env.REACT_APP_ENV === 'production' ? 'prod' : 'stage'
          }tracks/${trackId}/thumb_${timestamp}_${index}.jpg`

          const imageRef = ref(storage, imagePath)

          // Upload image
          const snapshot = await uploadBytes(imageRef, blob)

          // Get download URL
          const downloadURL = await getDownloadURL(snapshot.ref)

          // Generate and upload thumbnail if image
          let thumbnailURL = null
          try {
            const thumbnailBlob = await generateImageThumbnail(blob)
            if (thumbnailBlob) {
              const thumbRef = ref(storage, thumbPath)
              await uploadBytes(thumbRef, thumbnailBlob)
              thumbnailURL = await getDownloadURL(thumbRef)
            }
          } catch (thumbError) {

            console.error('Error generating thumbnail:', thumbError)
          }

          return {
            name: image.name,
            url: downloadURL,
            thumbnail: thumbnailURL
          }
        } catch (error) {
          console.error('Error uploading image:', error)
          throw error
        }
      })

      return await Promise.all(uploadPromises)
    } catch (error) {
      console.error('Firebase storage initialization error:', error)
      throw error
    }
  }
  return (
    <form onSubmit={handleSubmit}>
      <div
        style={{
          marginBottom: '15px',
          padding: '10px',
          background: '#f0f8ff',
          borderRadius: '4px'
        }}>
        <strong>Pin Location:</strong>
        <br />
        Lat: {latitude.toFixed(6)}, Lng: {longitude.toFixed(6)}
      </div>

      <TextField
        label='Label *'
        name='label'
        placeholder='Enter pin label (max 10 chars)'
        maxLength={10}
        required={true}
        {...getFieldProps()}
      />

      <TextAreaField
        label='Description (optional)'
        name='description'
        placeholder='Add description for this pin...'
        {...getFieldProps()}
      />

      <ColorField
        label='Pin Color *'
        name='color'
        required={true}
        defaultValue={'#f44336'}
        colors={INCIDENT_MAP_MARKER_COLORS}
        {...getFieldProps()}
      />

      {/* Image Upload Section */}
      <div style={{ marginBottom: '20px' }}>
        <label
          style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
          Add Images (Optional)
        </label>
        <input
          type='file'
          multiple
          accept='image/*'
          onChange={handleImageUpload}
          style={{
            width: '100%',
            padding: '8px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            marginBottom: '10px'
          }}
        />
        <small style={{ color: '#666' }}>
          Maximum 5 images, 5MB each. Supported formats: JPG, PNG, GIF
        </small>

        {/* Preview uploaded images */}
        {images.length > 0 && (
          <div style={{ marginTop: '10px' }}>
            <label
              style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: 'bold'
              }}>
              Uploaded Images ({images.length})
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
              {images.map((image, index) => (
                <div
                  key={index}
                  style={{ position: 'relative', width: '80px' }}>
                  <img
                    src={image.data}
                    alt={image.name}
                    style={{
                      width: '80px',
                      height: '80px',
                      objectFit: 'cover',
                      borderRadius: '4px',
                      border: '1px solid #ddd'
                    }}
                  />
                  <button
                    type='button'
                    onClick={() => removeImage(index)}
                    style={{
                      position: 'absolute',
                      top: '-5px',
                      right: '-5px',
                      background: '#eb144c',
                      color: 'white',
                      border: 'none',
                      borderRadius: '50%',
                      width: '20px',
                      height: '20px',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}>
                    ×
                  </button>
                  <div
                    style={{
                      fontSize: '10px',
                      textAlign: 'center',
                      marginTop: '4px'
                    }}>
                    {image.name.length > 10
                      ? image.name.substring(0, 10) + '...'
                      : image.name}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className='level'>
        <div className='level-left' />
        <div className='level-right'>
          <div className='level-item'>
            <Button
              type='submit'
              className={loading ? 'is-loading' : ''}
              enabled={`${!loading}`}>
              {loading ? 'Adding Pin...' : 'Add Pin to Map'}
            </Button>
          </div>
        </div>
      </div>
    </form>
  )
}
